import { IconPhotoOff } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';

/**
 * The image `load` gives for `source` (an id), shown while this element lives;
 * its object URL is released afterwards.
 */
export function XrayImage({
  source,
  load,
  alt,
  className,
}: {
  source: string;
  load: () => Promise<Blob>;
  alt: string;
  className?: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const loadRef = useRef(load);
  loadRef.current = load;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `load` changes every render; the image changes with `source`
  useEffect(() => {
    let alive = true;
    let created: string | null = null;
    setFailed(false);
    loadRef.current().then(
      (blob) => {
        if (!alive) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      },
      () => alive && setFailed(true)
    );
    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [source]);
  if (failed) {
    return (
      <span
        className={`xray-image xray-image--missing ${className ?? ''}`}
        role="img"
        aria-label={alt}
      >
        <IconPhotoOff size={28} aria-hidden="true" />
      </span>
    );
  }
  if (!url) return <span className={`xray-image xray-image--loading ${className ?? ''}`} />;
  return <img className={`xray-image ${className ?? ''}`} src={url} alt={alt} />;
}
